import type { StatusBadgeTone } from "@solomon-labs/design/components/ui";

/**
 * The retail position states that get a status badge, and the
 * design-system tone + label each one renders as.
 *
 * This is a mapping table, not a component. The badge itself is the
 * design system's `StatusBadge` (`@solomon-labs/design/components/ui`),
 * composed directly at each call site. The local `YaasPositionStatusPill`
 * wrapper that used to sit here was a duplicate of that component and is
 * archived; this table survives it because the state -> tone/label
 * mapping is the part that must not drift between the surfaces that
 * render the badge (PositionsSection, PositionsPanel,
 * YaasPositionForm).
 *
 * `disabled` and `inactive` are the two grey states: "switched off".
 * They differ only in who switched it off, and therefore in whether the
 * user has an affordance (Reactivate) beside the badge. Neither is an
 * error: a user who turned their own rewards off has nothing to fix.
 * `inactive` covers every admin-side API status a retail user cannot act on
 * — Suspended, Rejected, Hidden and Ended alike — because
 * `mapPositionStatus` (apps/main `hooks/yaas/helpers.ts`) folds all of
 * them into "Inactive" before a badge is ever derived.
 */
export type YaasPositionBadgeState = "earning" | "pending" | "error" | "disabled" | "inactive";

export const YAAS_POSITION_BADGE: Record<YaasPositionBadgeState, { tone: StatusBadgeTone; label: string }> = {
    earning: { tone: "success", label: "Earning" },
    pending: { tone: "pending", label: "Pending" },
    error: { tone: "error", label: "Ineligible" },
    disabled: { tone: "disabled", label: "Disabled" },
    inactive: { tone: "disabled", label: "Inactive" }
};

/**
 * Derive the badge state from a position's display status plus whether
 * its balance is under the minimum required to earn.
 *
 * Below-minimum applies only to "Earning" positions. A top-up cannot
 * resolve pending approval or an admin-side restriction, so those statuses
 * must retain their own badge and must not suggest that buying USDv is a fix.
 *
 * Everything that is not Earning or Inactive — Enable, Pending, or a
 * missing status — reads as `pending`.
 */
export function yaasPositionBadgeState(
    status: string | undefined,
    isBelowMinimum: boolean,
    isDisabledByUser = false
): YaasPositionBadgeState {
    if (isDisabledByUser) return "disabled";
    if (status === "Earning") return isBelowMinimum ? "error" : "earning";
    if (status === "Inactive") return "inactive";
    return "pending";
}
